/* global Office */
import React, { useState, useEffect } from "react";
import { LoginUser } from "../auth/authService";
import { VerificationService } from "../services/verificationService";
import { TemplateService } from "../services/templateService";
import { Session } from "../auth/session";
import { RemoteLogger } from "../services/remoteLogger";
import SelectVerifier from "./SelectVerifier";
import { Header } from "./Header";
import { IoArrowBackOutline } from "react-icons/io5";

interface ComposeViewProps {
  onBack: () => void;
  user: LoginUser | null;
  onLogout: () => void;
  onViewHistory: () => void;
}

interface ComposeDetails {
  subject: string;
  to: string;
  cc: string;
  bodyPreview: string;
}

const ComposeView: React.FC<ComposeViewProps> = ({ user, onBack, onLogout, onViewHistory }) => {
  const [details, setDetails] = useState<ComposeDetails>({
    subject: "",
    to: "",
    cc: "",
    bodyPreview: "",
  });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<
    "none" | "select-verifier" | "loading" | "requested" | "approved"
  >("none");
  const [lastVerificationId, setLastVerificationId] = useState<number | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [templateInfo, setTemplateInfo] = useState<{ id: number; fields: any[] } | null>(null);

  const inputFieldValues = React.useMemo(() => {
    if (!templateInfo) return [];

    const senderAddressField = templateInfo.fields.find((f: any) => (f.inputField?.key || f.key) === "sender_address");
    const domainField = templateInfo.fields.find((f: any) => (f.inputField?.key || f.key) === "domain");
    const subjectLineField = templateInfo.fields.find((f: any) => (f.inputField?.key || f.key) === "subject_line");

    const values = [];

    if (senderAddressField) {
      values.push({
        inputFieldId: senderAddressField.inputField?.id || senderAddressField.id,
        value: details.to || "Recipient",
      });
    }
    if (domainField) {
      const domain = details.to && details.to.includes("@") ? details.to.split("@")[1] : "";
      values.push({
        inputFieldId: domainField.inputField?.id || domainField.id,
        value: domain,
      });
    }
    if (subjectLineField) {
      values.push({
        inputFieldId: subjectLineField.inputField?.id || subjectLineField.id,
        value: details.subject || "Draft",
      });
    }

    if (values.length === 0) {
      values.push(
        {
          inputFieldId: 479,
          value: details.to || "Recipient",
        },
        {
          inputFieldId: 480,
          value: details.to && details.to.includes("@") ? details.to.split("@")[1] : "",
        },
        {
          inputFieldId: 481,
          value: details.subject || "Draft",
        }
      );
    }
    return values;
  }, [templateInfo, details.to, details.subject]);

  useEffect(() => {
    const loadComposeData = async () => {
      const item = Office.context.mailbox.item;
      if (!item) {
        setLoading(false);
        return;
      }

      const isAsync = (obj: any) => obj && typeof obj === "object" && "getAsync" in obj;

      const getSubject = (): Promise<string> =>
        new Promise((res) => {
          if (isAsync(item.subject))
            item.subject.getAsync((r: any) => res(r.value || "No Subject"));
          else res((item.subject as any) || "No Subject");
        });

      const getRecipients = (obj: any): Promise<string> =>
        new Promise((res) => {
          if (isAsync(obj))
            obj.getAsync((r: any) => {
              const list = r.value || [];
              res(
                list
                  .map((x: any) => x.emailAddress || x.displayName || "")
                  .filter(Boolean)
                  .join(", ") || ""
              );
            });
          else {
            const list = Array.isArray(obj) ? obj : [];
            res(
              list
                .map((r: any) => r.emailAddress || r.displayName || "")
                .filter(Boolean)
                .join(", ") || ""
            );
          }
        });

      const getBody = (): Promise<string> =>
        new Promise((res) => {
          if (item.body && isAsync(item.body)) {
            item.body.getAsync(Office.CoercionType.Text, (r: any) => {
              const text = r.status === Office.AsyncResultStatus.Succeeded ? r.value.trim() : "";
              res(text.length > 120 ? text.substring(0, 120) + "…" : text);
            });
          } else res("");
        });

      const [subject, to, cc, bodyPreview] = await Promise.all([
        getSubject(),
        getRecipients(item.to),
        getRecipients((item as any).cc),
        getBody(),
      ]);

      setDetails({ subject, to, cc, bodyPreview });
      setLoading(false);

      try {
        const token = await Session.getToken();
        if (token) {
          const templateList = await TemplateService.listAll(token);
          const tId = templateList.data?.data?.templateId;
          if (tId) {
            const templateDetail = await TemplateService.getById(tId, token);
            RemoteLogger.info("Fetched Template Details in ComposeView:", templateDetail);
            const fields = templateDetail.data?.data?.fields || [];
            setTemplateInfo({ id: tId, fields });
          }
        }
      } catch (err) {
        RemoteLogger.error("Failed to load template info in ComposeView:", err);
      }
    };

    loadComposeData();
    const handler = () => {
      setStatus("none");
      setLastVerificationId(null);
      setErrorMessage(null);
      setTimeout(() => loadComposeData(), 200);
    };
    Office.context?.mailbox?.addHandlerAsync(Office.EventType.ItemChanged, handler);
    return () => {
      Office.context?.mailbox?.removeHandlerAsync(Office.EventType.ItemChanged, handler);
    };
  }, []);

  useEffect(() => {
    if (status !== "requested" || !lastVerificationId) return;

    let intervalId: any;

    const checkApprovalStatus = async () => {
      try {
        const token = await Session.getToken();
        if (!token) return;

        const response = await VerificationService.getById(lastVerificationId, token);
        if (response.data) {
          const envelope = response.data as any;
          const actual = envelope?.data ?? envelope;
          const currentStatus = actual?.status;

          if (
            currentStatus === 2 ||
            (typeof currentStatus === "string" && currentStatus.toLowerCase() === "verified")
          ) {
            setStatus("approved");
            clearInterval(intervalId);
          } else if (
            currentStatus === 3 ||
            (typeof currentStatus === "string" && currentStatus.toLowerCase() === "rejected")
          ) {
            setStatus("none");
            setErrorMessage("This pre-approval request was rejected by the approver.");
            clearInterval(intervalId);
          }
        }
      } catch (error) {
        RemoteLogger.error("Error checking verification request status:", error);
      }
    };

    checkApprovalStatus();
    intervalId = setInterval(checkApprovalStatus, 10000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status, lastVerificationId]);

  const handleRequestApproval = async (
    verifierIds: number[],
    customQuestion?: string,
    organizationId?: number
  ) => {
    setErrorMessage(null);
    setStatus("loading");
    try {
      const token = await Session.getToken();
      if (!token) {
        RemoteLogger.warn("No token found, logging out...");
        onLogout();
        return;
      }

      let activeTemplateId = templateInfo?.id;
      let activeFields = templateInfo?.fields;

      if (!activeTemplateId || !activeFields) {
        const templateList = await TemplateService.listAll(token);
        const tId = templateList.data?.data?.templateId;
        if (!tId) {
          setErrorMessage("Could not retrieve template ID.");
          setStatus("none");
          return;
        }
        const templateDetail = await TemplateService.getById(tId, token);
        activeTemplateId = tId;
        activeFields = templateDetail.data?.data?.fields || [];
      }

      const senderAddressField = activeFields.find((f: any) => (f.inputField?.key || f.key) === "sender_address");
      const domainField = activeFields.find((f: any) => (f.inputField?.key || f.key) === "domain");
      const subjectLineField = activeFields.find((f: any) => (f.inputField?.key || f.key) === "subject_line");

      const payloadFields = [];

      if (senderAddressField) {
        payloadFields.push({
          inputFieldId: senderAddressField.inputField?.id || senderAddressField.id,
          value: details.to || "Recipient",
        });
      }
      if (domainField) {
        const domain = details.to && details.to.includes("@") ? details.to.split("@")[1] : "";
        payloadFields.push({
          inputFieldId: domainField.inputField?.id || domainField.id,
          value: domain,
        });
      }
      if (subjectLineField) {
        payloadFields.push({
          inputFieldId: subjectLineField.inputField?.id || subjectLineField.id,
          value: details.subject || "Draft",
        });
      }

      if (payloadFields.length === 0) {
        payloadFields.push(
          {
            inputFieldId: 479,
            value: details.to || "Recipient",
          },
          {
            inputFieldId: 480,
            value: details.to && details.to.includes("@") ? details.to.split("@")[1] : "",
          },
          {
            inputFieldId: 481,
            value: details.subject || "Draft",
          }
        );
      }

      // The template's "title" field is mandatory and becomes the verification title
      const title = "Pre-approval Request";
      const titleField = activeFields.find((f: any) => (f.inputField?.key || f.key) === "title");
      if (titleField) {
        payloadFields.push({
          inputFieldId: titleField.inputField?.id || titleField.id,
          value: title,
        });
      }

      const payload = {
        templateId: activeTemplateId,
        title,
        description: customQuestion || `Approval request for email to: ${details.to}`,
        senderContext: 2,
        // Personal requests send no org; the backend rejects orgs the sender is not an active member of
        senderOrganizationId: typeof organizationId === "number" ? organizationId : undefined,
        receiverContext: 2,
        receiverOrganizationId: typeof organizationId === "number" ? organizationId : undefined,
        input_field_values: payloadFields,
        assigneeIds: verifierIds,
      };

      RemoteLogger.info("[Approval] 🚀 Creating approval request...", payload);
      const response = await VerificationService.create(payload, token);
      RemoteLogger.info("[Approval] 📥 Response received:", response);

      if (response.data) {
        setStatus("requested");
        const actualData = (response.data as any).data;
        if (actualData?.id) setLastVerificationId(actualData.id);
      } else {
        if (response.status === 401) {
          onLogout();
        } else {
          setErrorMessage(response.error || "Failed to create approval request");
          setStatus("none");
        }
      }
    } catch (error: any) {
      RemoteLogger.error("Approval error:", error);
      setErrorMessage(error?.message || "An error occurred while requesting approval.");
      setStatus("none");
    }
  };

  if (status === "select-verifier") {
    return (
      <SelectVerifier
        emailSummary={{
          fromOrTo: `To: ${details.to || "Recipient"}`,
          subject: details.subject || "No Subject",
        }}
        onSelect={handleRequestApproval}
        onBack={() => setStatus("none")}
        onLogout={onLogout}
        templateId={templateInfo?.id || undefined}
        inputFieldValues={inputFieldValues}
      />
    );
  }

  const Shimmer = ({ w = "w-full", h = "h-3.5" }: { w?: string; h?: string }) => (
    <div className={`${w} ${h} rounded-lg bg-gray-100 animate-pulse`} />
  );

  return (
    <div className="flex flex-col w-full bg-gray-50 min-h-full">
      <Header title="Pre-approve email" onLogout={onLogout} />
      <button
        aria-label="Back"
        className="text-gray-500 hover:text-gray-700 px-4 mt-5"
        onClick={() => onBack()}
      >
        <IoArrowBackOutline size={16} />
      </button>

      {/* Error */}
      {errorMessage && (
        <div className="mx-3 mt-3 flex items-start gap-2.5 p-3 bg-red-50 border border-red-100 rounded-xl">
          <svg
            className="w-4 h-4 text-red-400 shrink-0 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="text-[12px] font-semibold text-red-600 flex-1 break-words">
            {errorMessage}
          </span>
          <button
            aria-label="Close"
            onClick={() => setErrorMessage(null)}
            className="text-red-400 hover:text-red-600 shrink-0"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}

      <main className="flex-1 px-3 py-4 flex flex-col gap-3">
        {/* Page title row */}
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-[15px] font-bold text-gray-900">Pre-approve email</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Get sign-off before sending sensitive info
            </p>
          </div>
          <button
            onClick={onViewHistory}
            className="text-[11px] font-semibold text-[#33ccbf] hover:underline bg-transparent border-none cursor-pointer mt-0.5"
          >
            History
          </button>
        </div>

        {/* Draft email card */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {/* Card header strip */}
          <div className="px-4 py-3 border-b border-gray-50 flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-[#33ccbf]/10 flex items-center justify-center shrink-0">
              <svg
                className="w-4 h-4 text-[#33ccbf]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <div>
              <p className="text-[11px] font-bold text-gray-800">Draft email</p>
              <p className="text-[10px] text-gray-400">Pending your send</p>
            </div>
          </div>

          {/* Subject — hero */}
          <div className="px-4 pt-3.5 pb-3 border-b border-gray-50">
            {loading ? (
              <div className="space-y-1.5">
                <Shimmer w="w-4/5" h="h-4" />
                <Shimmer w="w-1/2" h="h-3" />
              </div>
            ) : (
              <p className="text-[14px] font-bold text-gray-900 leading-snug">
                {details.subject || "No Subject"}
              </p>
            )}
          </div>

          {/* Fields */}
          <div className="divide-y divide-gray-50">
            {/* To */}
            <div className="px-4 py-2.5 flex items-start gap-3">
              <span className="text-[10px] font-bold text-gray-400 w-8 shrink-0 pt-0.5 uppercase tracking-wide">
                To
              </span>
              <div className="flex-1 min-w-0">
                {loading ? (
                  <Shimmer w="w-2/3" />
                ) : (
                  <p className="text-[12px] font-semibold text-gray-800 break-all">
                    {details.to || <span className="text-gray-300 font-normal">No recipients</span>}
                  </p>
                )}
              </div>
            </div>

            {/* CC — only if present */}
            {(loading || details.cc) && (
              <div className="px-4 py-2.5 flex items-start gap-3">
                <span className="text-[10px] font-bold text-gray-400 w-8 shrink-0 pt-0.5 uppercase tracking-wide">
                  CC
                </span>
                <div className="flex-1 min-w-0">
                  {loading ? (
                    <Shimmer w="w-1/2" />
                  ) : (
                    <p className="text-[12px] font-semibold text-gray-800 break-all">
                      {details.cc}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Body preview — only if present */}
            {(loading || details.bodyPreview) && (
              <div className="px-4 py-2.5 flex items-start gap-3">
                <span className="text-[10px] font-bold text-gray-400 w-8 shrink-0 pt-0.5 uppercase tracking-wide">
                  Body
                </span>
                <div className="flex-1 min-w-0">
                  {loading ? (
                    <div className="space-y-1.5">
                      <Shimmer />
                      <Shimmer w="w-4/5" />
                    </div>
                  ) : (
                    <p className="text-[11px] text-gray-500 leading-relaxed">
                      {details.bodyPreview}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* CTA / Success */}
        {status === "none" || status === "loading" ? (
          <>
            <button
              onClick={() => setStatus("select-verifier")}
              disabled={status === "loading"}
              className="w-full py-3.5 bg-[#33ccbf] hover:bg-[#2bbdb1] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-2xl text-[13px] font-bold flex items-center justify-center gap-2 transition-all shadow-md shadow-[#33ccbf]/25"
            >
              {status === "loading" ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Requesting...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    />
                  </svg>
                  Request Approval
                </>
              )}
            </button>

            <div className="flex items-start gap-2 px-0.5">
              <svg
                className="w-3.5 h-3.5 text-gray-300 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                strokeWidth="2"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p className="text-[10px] text-gray-400 leading-relaxed">
                Your chosen approver will receive a request and must confirm before you send.
              </p>
            </div>
          </>
        ) : status === "approved" ? (
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <div className="h-1 bg-emerald-500" />
            <div className="px-4 py-5 text-center">
              <div className="w-12 h-12 bg-emerald-50 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-emerald-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-[14px] font-bold text-gray-900 mb-1">Email Approved</p>
              <p className="text-[11px] text-gray-500 leading-relaxed max-w-[220px] mx-auto">
                This email is pre-approved and safe to send. You can send it now without any warnings.
              </p>
              {lastVerificationId && (
                <button
                  onClick={onViewHistory}
                  className="mt-4 text-[11px] font-bold text-[#33ccbf] bg-[#33ccbf]/10 hover:bg-[#33ccbf]/20 border border-[#33ccbf]/20 px-4 py-1.5 rounded-full transition-colors"
                >
                  View in History
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
            <div className="h-1 bg-[#33ccbf]" />
            <div className="px-4 py-5 text-center">
              <div className="w-12 h-12 bg-[#33ccbf]/10 rounded-2xl flex items-center justify-center mx-auto mb-3">
                <svg
                  className="w-6 h-6 text-[#33ccbf]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2.5"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-[14px] font-bold text-gray-900 mb-1">Approval Requested</p>
              <p className="text-[11px] text-gray-400 leading-relaxed max-w-[220px] mx-auto">
                We'll notify you once a contact approves this email.
              </p>
              {lastVerificationId && (
                <button
                  onClick={onViewHistory}
                  className="mt-4 text-[11px] font-bold text-[#33ccbf] bg-[#33ccbf]/10 hover:bg-[#33ccbf]/20 border border-[#33ccbf]/20 px-4 py-1.5 rounded-full transition-colors"
                >
                  View in History
                </button>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ComposeView;
